import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Sparkles, Check, ArrowRight, Calendar } from "lucide-react";

const PRICING_TIERS = [
  {
    name: "Prompt",
    price: "$99",
    period: "/mo",
    tagline: "Direction & accountability",
    features: [
      "3 personalized prompts/week",
      "Prompts aligned to your content pillars",
      "Organized digital archive",
      "You create & publish your content",
    ],
    highlight: false,
    cta: "Get Started",
  },
  {
    name: "Essential",
    price: "$450",
    period: "/mo",
    tagline: "Prompts + written posts",
    features: [
      "3 personalized prompts/week",
      "2 written posts/week (8/month)",
      "Scheduling & posting (2 platforms)",
      "One revision per post",
    ],
    highlight: true,
    cta: "Most Popular",
  },
  {
    name: "Strategic",
    price: "$950",
    period: "/mo",
    tagline: "Full content leadership",
    features: [
      "Everything in Essential",
      "Posting up to 3 platforms",
      "Blog development (1/month)",
      "Quarterly strategy call",
    ],
    highlight: false,
    cta: "Get Started",
  },
];

const PromptsPreview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem("coc_prompts");
    if (!saved) {
      navigate("/");
      return;
    }
    const parsed = JSON.parse(saved);
    setData(parsed);

    const timers = [];
    (parsed.prompts || []).forEach((_, i) => {
      timers.push(setTimeout(() => setVisibleCount(i + 1), i * 250 + 300));
    });
    return () => timers.forEach(clearTimeout);
  }, [navigate]);

  if (!data) return null;

  const businessName = data.business_name || "your business";

  return (
    <div className="min-h-screen bg-[#FFFDF9] px-4 py-10">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-[#A8E6CF]/40 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Check size={14} /> Your prompts are ready!
          </div>
          <h1
            style={{ fontFamily: "'Young Serif', Georgia, serif" }}
            className="text-4xl font-bold text-[#2D3436] tracking-tight mb-2"
          >
            Your personalized<br />content lineup
          </h1>
          <p className="text-[#636E72] text-sm">
            These arrive by text every 2 days — starting tomorrow.
          </p>
        </div>

        {/* SMS thread */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-[#F1F3F5] p-5 mb-5">
          {/* Phone header */}
          <div className="flex items-center gap-3 pb-4 mb-4 border-b border-[#F1F3F5]">
            <div className="w-9 h-9 rounded-full bg-[#FF6B6B] flex items-center justify-center">
              <MessageCircle size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#2D3436]">Content on Cue</p>
              <p className="text-xs text-[#636E72]">
                Starting tomorrow · 1 prompt every 2 days
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {(data.prompts || []).map((prompt, i) => (
              <div
                data-testid={`prompt-card-${i}`}
                key={i}
                className="transition-all duration-500"
                style={{
                  opacity: i < visibleCount ? 1 : 0,
                  transform: i < visibleCount ? "translateY(0)" : "translateY(8px)",
                }}
              >
                <div className="flex gap-2.5 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFE3E3] flex items-center justify-center mt-0.5">
                    <span className="text-[10px] font-bold text-[#FF6B6B]">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="bg-[#F8F9FA] border border-[#EAEAEA] rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-[#2D3436] leading-relaxed">
                        {prompt.text}
                      </p>
                    </div>
          <p className="text-[11px] text-[#636E72] mt-1 ml-1">
                      Day {i * 2 + 1} · {prompt.category?.replace(/_/g, " ")}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* CTA message */}
            {data.cta && (
              <div
                data-testid="prompt-cta-card"
                className="transition-all duration-700"
                style={{
                  opacity: visibleCount >= (data.prompts?.length || 0) ? 1 : 0,
                  transform:
                    visibleCount >= (data.prompts?.length || 0)
                      ? "translateY(0)"
                      : "translateY(8px)",
                }}
              >
                <div className="flex gap-2.5 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF6B6B] flex items-center justify-center mt-0.5">
                    <Sparkles size={11} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="bg-[#FF6B6B] rounded-2xl rounded-tl-sm px-4 py-3">
                      <p className="text-sm text-white leading-relaxed">
                        {data.cta.text?.replace("[SIGNUP_LINK]", "contentoncue.com")}
                      </p>
                    </div>
                    <p className="text-[11px] text-[#636E72] mt-1 ml-1">
                      Day 16 · Invitation to continue
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* What to expect */}
        <div className="bg-[#FFF5F5] rounded-3xl p-5 mb-8 border border-[#FFE3E3]">
          <h3
            style={{ fontFamily: "'Young Serif', Georgia, serif" }}
            className="font-bold text-[#2D3436] text-xl mb-3"
          >
            What happens next?
          </h3>
          <div className="space-y-2">
            {[
              "Your first prompt arrives tomorrow",
              "One text every 2 days for about a week",
              "Just reply to capture your story",
              "On day 9, you'll get an invitation to continue",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <Check size={14} className="text-[#FF6B6B] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#2D3436]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA to Success */}
        <a
          data-testid="claim-trial-button"
          href="https://www.contentoncue.com/trial-confirmation"
          className="w-full bg-[#FF6B6B] text-white font-bold rounded-full px-8 py-4 shadow-[0_8px_20px_rgba(255,107,107,0.3)] hover:shadow-[0_12px_28px_rgba(255,107,107,0.4)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2 text-lg mb-8"
        >
          I'm in — let's do this! <ArrowRight size={20} />
        </a>

        <p
          data-testid="prompts-footer-note"
          className="text-center text-xs text-[#636E72] pb-8"
        >
          Your trial prompts are already queued. No credit card required.
        </p>
      </div>
    </div>
  );
};

export default PromptsPreview;
